import {
  DocumentExcelPane,
  DocumentPdfPane,
  DocumentViewer,
  type DocumentPaneRenderProps,
  type DocumentViewerLayout,
} from '@aecom-ai-engineering/react-components';
import {
  useGetBlobQuery,
  useGetBlobReadUrlQuery,
  usePackageId,
  useProjectId,
} from '@consigli/hooks';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DocumentLoadingPane } from '@/features/document-viewer/document-loading-pane';
import { toExcelHighlight, toHighlights } from '@/features/document-viewer/excel-highlight';
import { ScheduleContextSidebar } from '@/features/document-viewer/schedule-context-sidebar';
import {
  ComponentReference,
  ScheduleComponent,
} from '@/pages/schedule-check/components/component-card';
import { isExcelDocumentType } from '@/util/document-check';

interface ScheduleDoubleViewerProps {
  schedule: ComponentReference;
  clicked: ComponentReference;
  component: ScheduleComponent;
  isPreview: boolean;
  onClose?: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  hasNext?: boolean;
  hasPrevious?: boolean;
}

export const ScheduleDoubleViewer: FC<ScheduleDoubleViewerProps> = ({
  schedule,
  clicked,
  component,
  onClose,
  onNext,
  onPrevious,
  hasNext = false,
  hasPrevious = false,
}) => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();

  const { data: scheduleBlob } = useGetBlobQuery(
    { projectId, packageId, blobId: schedule.blobId! },
    { skip: !schedule.blobId },
  );

  const { data: clickedBlob } = useGetBlobQuery(
    { projectId, packageId, blobId: clicked.blobId! },
    { skip: !clicked.blobId },
  );

  const { data: scheduleReadUrl } = useGetBlobReadUrlQuery(
    { projectId, packageId, blobId: schedule.blobId! },
    { skip: !schedule.blobId },
  );

  const { data: clickedReadUrl } = useGetBlobReadUrlQuery(
    { projectId, packageId, blobId: clicked.blobId! },
    { skip: !clicked.blobId },
  );

  const scheduleIsExcel = !!schedule.fileName && isExcelDocumentType(schedule.fileName);
  const clickedIsExcel = !!clicked.fileName && isExcelDocumentType(clicked.fileName);

  // Excel loads from the blob's download URL; everything else (PDF) needs a
  // signed read URL. Both panes must resolve a URL for either file type — the
  // guard below returns a loader until they do, so a schedule with no URL would
  // otherwise leave the viewer stuck loading and the finding never opening.
  const scheduleSrc = scheduleIsExcel
    ? (scheduleBlob?.downloadUrl ?? '')
    : (scheduleReadUrl?.readUrl ?? '');
  const clickedSrc = clickedIsExcel
    ? (clickedBlob?.downloadUrl ?? '')
    : (clickedReadUrl?.readUrl ?? '');

  const scheduleExcelHighlight = useMemo(
    () => (scheduleIsExcel ? toExcelHighlight(schedule.cellCoordinates) : undefined),
    [scheduleIsExcel, schedule.cellCoordinates],
  );
  const clickedExcelHighlight = useMemo(
    () => (clickedIsExcel ? toExcelHighlight(clicked.cellCoordinates) : undefined),
    [clickedIsExcel, clicked.cellCoordinates],
  );
  const scheduleInitialPage = scheduleIsExcel ? undefined : (schedule.page ?? undefined);
  const clickedInitialPage = clickedIsExcel ? undefined : (clicked.page ?? undefined);

  // Draw the finding's bounding box on the PDF panes. `page` is already 1-based
  // (parseFileMetadata adds 1), while toHighlights expects a 0-based pageIndex
  // and re-adds 1, so hand it `page - 1` to land on the right page.
  const schedulePdfHighlights = useMemo(
    () =>
      !scheduleIsExcel && schedule.coordinates && schedule.page
        ? toHighlights({ ...schedule.coordinates, pageIndex: schedule.page - 1 })
        : undefined,
    [scheduleIsExcel, schedule.coordinates, schedule.page],
  );
  const clickedPdfHighlights = useMemo(
    () =>
      !clickedIsExcel && clicked.coordinates && clicked.page
        ? toHighlights({ ...clicked.coordinates, pageIndex: clicked.page - 1 })
        : undefined,
    [clickedIsExcel, clicked.coordinates, clicked.page],
  );

  // Swapping `src` on a live AECOM pane can wedge it on "Rendering page"; keying
  // by document + page remounts only the changed pane. Role prefix keeps keys
  // distinct if both panes ever resolve to the same blob.
  const schedulePaneKey = `primary-${schedule.blobId ?? ''}#${schedule.page ?? ''}`;
  const clickedPaneKey = `secondary-${clicked.blobId ?? ''}#${clicked.page ?? ''}`;

  // While a signed URL resolves `props.src` is empty; render a spinner in that
  // pane so the drawer opens immediately (and the loaded pane shows straight
  // away when only one side is still resolving) instead of flashing a loader at
  // the bottom of the page.
  const renderers = useMemo(
    () => ({
      excel: (props: DocumentPaneRenderProps) =>
        props.src ? (
          <DocumentExcelPane
            key={props.src === scheduleSrc ? schedulePaneKey : clickedPaneKey}
            active={props.active}
            caption={props.caption}
            fileName=""
            highlight={props.src === scheduleSrc ? scheduleExcelHighlight : clickedExcelHighlight}
            searchable
            src={props.src}
          />
        ) : (
          <DocumentLoadingPane caption={props.caption} />
        ),
      pdf: (props: DocumentPaneRenderProps) =>
        props.src ? (
          <DocumentPdfPane
            key={props.src === scheduleSrc ? schedulePaneKey : clickedPaneKey}
            active={props.active}
            caption={props.caption}
            onSidebarContextChange={props.onSidebarContextChange}
            src={props.src}
            initialPage={props.src === scheduleSrc ? scheduleInitialPage : clickedInitialPage}
            highlights={props.src === scheduleSrc ? schedulePdfHighlights : clickedPdfHighlights}
            searchable
          />
        ) : (
          <DocumentLoadingPane caption={props.caption} />
        ),
    }),
    [
      scheduleSrc,
      scheduleExcelHighlight,
      clickedExcelHighlight,
      scheduleInitialPage,
      clickedInitialPage,
      schedulePdfHighlights,
      clickedPdfHighlights,
      schedulePaneKey,
      clickedPaneKey,
    ],
  );

  const maximizedRef = useRef(false);
  const [layout, setLayout] = useState<DocumentViewerLayout>('stacked');

  const handleExpand = useCallback(() => {
    maximizedRef.current = !maximizedRef.current;
    setLayout(maximizedRef.current ? 'side-by-side' : 'stacked');
  }, []);

  const scheduleLabel = schedule.fileName ?? t('schedule-double-viewer.schedule-pane-label');
  const clickedLabel = clicked.fileName ?? t('schedule-double-viewer.document-pane-label');

  return (
    <DocumentViewer
      open
      onOpenChange={(details) => {
        if (!details.open) onClose?.();
      }}
      src={scheduleSrc}
      fileType={scheduleIsExcel ? 'excel' : undefined}
      fileName={schedule.fileName}
      secondarySrc={clickedSrc}
      secondaryFileType={clickedIsExcel ? 'excel' : undefined}
      secondaryFileName={clicked.fileName}
      title={scheduleLabel}
      headerLabel={component.name}
      primaryTitle={scheduleLabel}
      secondaryTitle={clickedLabel}
      layout={layout}
      onLayoutChange={setLayout}
      onExpand={handleExpand}
      showSidebar
      showNavigationActions
      previousDisabled={!hasPrevious}
      nextDisabled={!hasNext}
      onPrevious={onPrevious}
      onNext={onNext}
      renderers={renderers}
    >
      <ScheduleContextSidebar component={component} />
    </DocumentViewer>
  );
};
